import { React, useState, Fragment } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf, faPaperPlane, faUsers, faCalendarDays, faBolt, faNewspaper, faCircleInfo, faEnvelope, faBell } from '@fortawesome/free-solid-svg-icons'

import Skeleton from '../Skeleton/Skeleton.js'
import DownloadButton from '../DownloadButton/DownloadButton.js'
import { formatDateWithTime, downloadPdf } from '../../main.js'

import Cookies from 'universal-cookie'

import './MailingMessageList.css'
import './MMailingMessageList.css'

// Synchron mit AdminMailing / mail/layout.js (Plattform-**fett** / *kursiv*)
const INLINE_FORMAT_RE = /(^|[^\p{L}\p{N}*])(?:\*\*(\S(?:.*?\S)??)\*\*|\*([^\s*](?:.*?[^\s*])??)\*)(?![\p{L}\p{N}*])/u

function stripInlineFormatting(text) {
    return String(text || '')
        .split(/\r\n|\r|\n/)
        .map(function strip(line) {
            const re = new RegExp(INLINE_FORMAT_RE.source, 'gu')
            return line.replace(re, (match, prefix, bold, italic) => prefix + strip(bold !== undefined ? bold : italic))
        })
        .join('\n')
}

function renderInlineFormatting(line) {
    const nodes = []
    const re = new RegExp(INLINE_FORMAT_RE.source, 'gu')
    let last = 0
    let match
    while ((match = re.exec(line)) !== null) {
        const start = match.index + match[1].length
        if (start > last) nodes.push(line.slice(last, start))
        const isBold = match[2] !== undefined
        const inner = renderInlineFormatting(isBold ? match[2] : match[3])
        nodes.push(isBold ? <strong key={start}>{inner}</strong> : <em key={start}>{inner}</em>)
        last = re.lastIndex
    }
    if (last < line.length) nodes.push(line.slice(last))
    return nodes
}

function renderFormattedPlainText(text) {
    return String(text || '').split(/\r\n|\r|\n/).map((line, index) => (
        <Fragment key={index}>{index > 0 ? <br /> : null}{renderInlineFormatting(line)}</Fragment>
    ))
}

/** Ersten Buchstaben groß – für Preview ohne Anrede (Mail-Body startet oft klein). */
export function capitalizeFirstLetter(text) {
    const s = String(text || '')
    const match = s.match(/^(\s*)(\S)([\s\S]*)$/u)
    if (!match) return s
    return match[1] + match[2].toLocaleUpperCase('de-DE') + match[3]
}

export function plainMessagePreview(htmlOrText, maxLen = 100, { capitalize = false } = {}) {
    if (!htmlOrText) return '—'
    let plain = stripInlineFormatting(String(htmlOrText))
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    if (!plain) return '—'
    if (capitalize) plain = capitalizeFirstLetter(plain)
    if (plain.length <= maxLen) return plain
    return plain.slice(0, maxLen).trim() + '…'
}

function greetingLabel(firstname, lastname) {
    const name = [firstname, lastname].filter(Boolean).join(' ').trim()
    return name ? `Hallo ${name},` : 'Hallo,'
}

/**
 * Wiederverwendbare Liste versendeter Rundmails.
 *
 * props:
 *  - items: Array (API-Objekte aus /mailing/history bzw. /mailing/myhistory)
 *  - loaded: bool – false zeigt Skeleton
 *  - emptyText: string – Text bei leerer Liste
 *  - showRecipients: bool – Empfängernamen im Detail (nur Manager-Sicht)
 *  - showRunId: bool – RunId-Zeile (nur Manager-Sicht)
 *  - showEvent: bool – Eventname je Eintrag (Gesamtliste über mehrere Events)
 *  - showReadState: bool – Gelesen/Ungelesen-Markierung (Empfänger-Sicht)
 *  - onMarkRead: fn(id) – wird beim Öffnen einer ungelesenen Nachricht aufgerufen
 *  - allowPdfDownload: bool – Dienstplan-PDF für den eingeloggten Nutzer herunterladbar
 */
export default function MailingMessageList({
    items = [],
    loaded = false,
    emptyText = 'Noch keine Nachrichten.',
    showRecipients = false,
    showRunId = false,
    showEvent = false,
    showReadState = false,
    onMarkRead = null,
    allowPdfDownload = false,
}) {
    const [expandedId, setExpandedId] = useState(null)

    const cookies = new Cookies()
    const cur_userid = cookies.get('userid')
    const cur_authtoken = cookies.get('authtoken')
    const cur_firstname = cookies.get('firstname')
    const cur_lastname = cookies.get('lastname')

    function toggle(entry) {
        const willOpen = expandedId !== entry._id
        setExpandedId(willOpen ? entry._id : null)
        if (willOpen && showReadState && !entry.read && typeof onMarkRead === 'function') {
            onMarkRead(entry._id)
        }
    }

    function downloadSchedulePdf(entry) {
        if (!entry.eventid || !cur_userid) return Promise.reject(new Error('Fehlende Event- oder User-ID'))
        return downloadPdf(
            "/mailing/getscheduleplanpdf/" + entry.eventid + "/" + cur_userid,
            'Dienstplan' + (entry.eventName ? '_' + entry.eventName.replace(/[^\w-]+/g, '_') : '') + '.pdf',
            cur_authtoken
        )
    }

    if (!loaded) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Skeleton height="64px" />
                <Skeleton height="64px" />
            </div>
        )
    }

    if (!items.length) {
        return <p className='mailings-history-empty'>{emptyText}</p>
    }

    return (
        <ul className='mailings-history-list'>
            {items.map(function (entry) {
                const isOpen = expandedId === entry._id
                const isUnread = showReadState && !entry.read
                const isPlatform = !!entry.isPlatform
                const isNewsletter = isPlatform && entry.kind !== 'information' && entry.newsletterOnly !== false
                const sentLabel = entry.sentAt ? formatDateWithTime(new Date(entry.sentAt)) : '—'
                const senderLabel = entry.sender
                    ? (entry.sender.firstname + ' ' + entry.sender.lastname)
                    : 'Unbekannt'
                return (
                    <li key={entry._id} className={'mailings-history-item' + (isOpen ? ' is-open' : '') + (isUnread ? ' is-unread' : '')}>
                        <div
                            className='mailings-history-summary'
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(entry)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    toggle(entry)
                                }
                            }}
                            aria-expanded={isOpen ? 'true' : 'false'}
                        >
                            {(showEvent) ? (
                                <div className='mailings-history-event'>
                                    <FontAwesomeIcon icon={isPlatform ? faBolt : faCalendarDays} />
                                    {' '}
                                    {isPlatform ? 'Helferzeit' : (entry.eventName || 'für dieses Event')}
                                </div>
                            ) : null}
                            <div className='mailings-history-top'>
                                <div className='mailings-history-meta'>
                                    {isUnread ? <span className='mailings-history-dot' title="Ungelesen" aria-label="Ungelesen" /> : null}
                                    <span className='mailings-history-date'>{sentLabel}</span>
                                    <span className='mailings-history-sender'>von {senderLabel}</span>
                                </div>
                                <div className='mailings-history-badges'>
                                    {isUnread ? <span className='badge badge-brand' title="Ungelesen">Neu</span> : null}
                                    {isPlatform ? (
                                        isNewsletter ? (
                                            <span className='badge badge-info' title="Newsletter">
                                                <FontAwesomeIcon icon={faNewspaper} /> Newsletter
                                            </span>
                                        ) : (
                                            <span className='badge badge-brand' title="Information">
                                                <FontAwesomeIcon icon={faCircleInfo} /> Information
                                            </span>
                                        )
                                    ) : null}
                                    {isPlatform ? (
                                        entry.profileOnly ? (
                                            <span
                                                className='badge badge-accent badge-icon'
                                                title="Benachrichtigung – nur in deinem Profil (keine E-Mail)"
                                                aria-label="Benachrichtigung – nur in deinem Profil (keine E-Mail)"
                                            >
                                                <FontAwesomeIcon icon={faBell} />
                                            </span>
                                        ) : (
                                            <span
                                                className='badge badge-brand badge-icon'
                                                title="Per E-Mail versendet und in deinem Profil gespeichert"
                                                aria-label="Per E-Mail versendet und in deinem Profil gespeichert"
                                            >
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </span>
                                        )
                                    ) : null}
                                    {showRecipients ? (
                                        <span className='badge badge-info' title="Empfänger">
                                            <FontAwesomeIcon icon={faUsers} /> {entry.recipientCount}
                                        </span>
                                    ) : null}
                                    {!isPlatform && entry.includeSchedulePdf ? (
                                        <span className='badge badge-brand' title="Mit Dienstplan-PDF">
                                            <FontAwesomeIcon icon={faFilePdf} /> PDF
                                        </span>
                                    ) : (!isPlatform ? (
                                        <span className='badge badge-accent' title="Ohne PDF">
                                            <FontAwesomeIcon icon={faPaperPlane} /> Nachricht
                                        </span>
                                    ) : null)}
                                </div>
                            </div>
                            {(isPlatform && entry.subject) ? (
                                <b className='mailings-history-subject'>{entry.subject}</b>
                            ) : null}
                            <p className='mailings-history-preview'>
                                {plainMessagePreview(entry.messageText, 100, { capitalize: isPlatform })}
                            </p>
                            {(showRunId && entry.runid) ? (
                                <small
                                    className='mailings-history-runid'
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    Für Debugging: RunId=
                                    <span className='mailings-history-runid-value' title="Zum Kopieren markieren">{entry.runid}</span>
                                </small>
                            ) : null}
                        </div>
                        {isOpen ? (
                            <div className='mailings-history-detail'>
                                <div className='mailings-history-message'>
                                    {isPlatform ? null : <b>Nachrichtentext</b>}
                                    {isPlatform ? (
                                        <div className='mailings-history-message-body'>
                                            {entry.messageText ? (
                                                <>
                                                    <p className='mailings-history-greeting'>{greetingLabel(cur_firstname, cur_lastname)}</p>
                                                    {renderFormattedPlainText(entry.messageText)}
                                                </>
                                            ) : <em>Kein Individualtext</em>}
                                        </div>
                                    ) : (
                                        <div
                                            className='mailings-history-message-body'
                                            dangerouslySetInnerHTML={{
                                                __html: entry.messageText
                                                    ? String(entry.messageText)
                                                    : '<em>Kein Individualtext</em>',
                                            }}
                                        />
                                    )}
                                </div>
                                {(allowPdfDownload && entry.includeSchedulePdf) ? (
                                    <div className='mailings-history-pdf'>
                                        <DownloadButton size="sm" title="Dienstplan herunterladen" onDownload={() => downloadSchedulePdf(entry)} />
                                    </div>
                                ) : null}
                                {(showRecipients && Array.isArray(entry.recipients)) ? (
                                    <div className='mailings-history-recipients'>
                                        <b>Empfänger ({entry.recipientCount})</b>
                                        <ul>
                                            {entry.recipients.map(function (r) {
                                                return (
                                                    <li key={String(r._id)}>
                                                        {r.firstname} {r.lastname}
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </li>
                )
            })}
        </ul>
    )
}
